'use client'
import { Box, SimpleGrid, Skeleton, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import React, { Suspense } from 'react'

import { useStockContext } from '@/app/[locale]/price/stocks/[symbol]/_contexts/StockContext'
import { SectionErrorBoundary } from '@/components/error/boundary'
import { DEFAULT_CURRENCY, getCurrencyInfo } from '@/configs/currencies'
import { useSsrContext } from '@/contexts/ssr'
import { useCurrencyAmounts } from '@/hooks/business/useCurrencyAmounts'
import { formatMarketCap, formatPercentageWithLimit } from '@/utils/number/format'

import InfoTooltip from './InfoTooltip'

function MarketDataFallback() {
  return (
    <Box
      mt={80}
      style={{
        borderRadius: 12,
        width: '100%',
      }}
    >
      <Skeleton
        h={32}
        w={160}
        radius={40}
        mb={12}
        style={{ backgroundColor: 'var(--bg-surface-secondary)', borderRadius: 40 }}
      />
      <SimpleGrid cols={{ base: 2, md: 4 }} spacing={16}>
        {[0, 1, 2, 3, 4, 5, 6, 7].map(index => (
          <Box key={index}>
            <Skeleton
              h={16}
              w={120}
              mb={8}
              style={{ backgroundColor: 'var(--bg-surface-secondary)', borderRadius: 40 }}
            />
            <Skeleton
              h={20}
              w={80}
              style={{ backgroundColor: 'var(--bg-surface-secondary)', borderRadius: 40 }}
            />
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  )
}

function MarketDataContent() {
  const { stock } = useStockContext()
  const marketDataT = useTranslations('marketData')
  const coinTableT = useTranslations('coinTable.headers')
  const { selectedCurrency } = useSsrContext()
  const currentCurrency = getCurrencyInfo(selectedCurrency) || DEFAULT_CURRENCY

  const marketCap = stock?.usd_marketcap
  const volume = stock?.usd_volume

  const {
    amounts: [{ formattedValue: formattedMarketCap }, { formattedValue: formattedVolume }],
  } = useCurrencyAmounts({
    amounts: [{ value: marketCap }, { value: volume }],
    tokenSymbol: stock.symbol,
    valueFormatter: formatMarketCap,
    currencyFormatOptions: {},
  })

  if (!stock) return null

  const dividentYieldPercent = formatPercentageWithLimit(stock?.usd_dividend_yield || 0)

  const contentItems = [
    {
      title: coinTableT('marketCap'),
      content: <Text variant="body1">{formattedMarketCap}</Text>,
      value: marketCap,
    },
    {
      title: marketDataT('items.volume'),
      content: <Text variant="body1">{`${formattedVolume}`}</Text>,
      value: stock.usd_volume,
    },
    {
      title: marketDataT('items.price_earning_rate'),
      content: <Text variant="body1">{stock.usd_pe}</Text>,
      value: stock.usd_pe,
    },
    {
      title: marketDataT('items.earning_per_share'),
      content: (
        <Text variant="body">{`${currentCurrency.symbol}${(stock.usd_earning_per_share || 0).toFixed(2)}`}</Text>
      ),
      value: stock.usd_earning_per_share,
    },
    {
      title: marketDataT('items.52_week_high'),
      content: (
        <Text variant="body">{`${currentCurrency.symbol}${(stock.usd_52_week_high || 0).toFixed(2)}`}</Text>
      ),
      value: stock.usd_52_week_high,
    },
    {
      title: marketDataT('items.52_week_low'),
      content: (
        <Text variant="body">{`${currentCurrency.symbol}${(stock.usd_52_week_low || 0).toFixed(2)}`}</Text>
      ),
      value: stock.usd_52_week_low,
    },
    {
      title: marketDataT('items.dividend_yield'),
      content: <Text variant="body1">{`${dividentYieldPercent}`}</Text>,
      value: stock?.usd_dividend_yield,
    },
    {
      title: marketDataT('items.dividend_per_share'),
      content: (
        <Text variant="body1">{`${currentCurrency.symbol}${(stock.usd_dividend_per_share || 0).toFixed(1)}`}</Text>
      ),
      value: stock.usd_dividend_per_share,
    },
  ]

  const filteredContentItems = contentItems.filter(item => {
    if (item.value === 0 || item.value === null || item.value === undefined) {
      return false
    }
    return true
  })

  return (
    <Box
      mt={{ base: 40, medium: 80 }}
      style={{
        borderRadius: 12,
        width: '100%',
      }}
    >
      <Text component="h2" variant="heading2" mb={12}>
        {marketDataT('title')}
      </Text>
      <SimpleGrid cols={{ base: 2, medium: 4 }} spacing={16}>
        {filteredContentItems.map((item, index) => (
          <InfoTooltip key={index} title={item.title} content={item.content} />
        ))}
      </SimpleGrid>
    </Box>
  )
}

function MarketData() {
  return (
    <Suspense fallback={<MarketDataFallback />}>
      <MarketDataContent />
    </Suspense>
  )
}

export default SectionErrorBoundary.with(MarketData)
